
import React, { useEffect, useMemo, useState } from 'react';
import { uploadFile } from '../api/uploadApi';
import { resolveAssetUrl } from '../utils/url';

type FileUploaderProps = {
    onUpload?: (url: string) => void;
    onClear?: () => void;
    label?: string;
    accept?: string;
    initialUrl?: string | null;
};

export function FileUploader({ onUpload, onClear, label, accept, initialUrl }: FileUploaderProps) {
    const normalizedInitial = useMemo(() => resolveAssetUrl(initialUrl || undefined) || initialUrl || null, [initialUrl]);
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(normalizedInitial);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Reset stato locale quando cambia l'url iniziale (es. in fase di edit)
        setPreview(normalizedInitial);
        setFile(null);
        setError(null);
    }, [normalizedInitial]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        setError(null);
        if (f && f.type.startsWith('image')) {
            setPreview(URL.createObjectURL(f));
        } else {
            setPreview(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        setError(null);
        try {
            const result = await uploadFile(file);
            if (result.url) {
                if (onUpload) onUpload(result.url);
                setPreview(resolveAssetUrl(result.url) || result.url);
            }
        } catch (err: any) {
            setError('Errore upload: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFile(null);
        setPreview(null);
        setError(null);
        if (onClear) onClear();
    };

    return (
        <div className="file-uploader-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', background: '#fff', borderRadius: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem', minWidth: 180 }}>
            {label && <span style={{ fontWeight: 500 }}>{label}</span>}
            <input type="file" onChange={handleFileChange} accept={accept || "image/*,.pdf,.doc,.docx"} />
            {preview && (
                <img src={preview} alt="preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '0.7rem', marginTop: '0.5rem' }} />
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleUpload} disabled={!file || loading} style={{ background: '#e91e63', color: '#fff', border: 'none', borderRadius: '0.7rem', padding: '0.4rem 1rem', fontWeight: 500, cursor: 'pointer' }}>
                    {loading ? 'Caricamento...' : 'Carica'}
                </button>
                {(preview || normalizedInitial) && (
                    <button onClick={handleClear} type="button" disabled={loading} style={{ background: '#f0f0f0', color: '#444', border: 'none', borderRadius: '0.7rem', padding: '0.4rem 1rem', fontWeight: 500, cursor: 'pointer' }}>
                        Rimuovi
                    </button>
                )}
            </div>
            {error && <span style={{ color: 'red', fontSize: '0.9rem' }}>{error}</span>}
        </div>
    );
}